import {RcfaComplianceTableData} from '@domain/rcfa'
import {HierarchyNodeType} from '@domain/shared'
import {CollapsableRow, expandableTableHeaderCellSx} from '@hconnect/uikit/src/lib2'
import {Table, TableContainer, TableBody, TableCell, TableHead, TableRow, Box} from '@mui/material'
import {isEqual} from 'lodash'
import React, {useCallback} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../consts'
import {getCompletedMandatoryRcfasText} from '../utils'

import {HeaderWithTooltip} from './HeaderWithTooltip'
import {renderRcfaComplianceTableCell, RCFA_COMPLIANCE_TABLE_KEYS} from './RcfaComplianceTableRow'

type Props = {
  data: RcfaComplianceTableData[]
  getIsExpanded: (itemId: string) => boolean
  toggleExpandCollapse: (itemId: string) => void
  onPlantItemClick: (plantId: string) => void
}

const collapseToggleTestIdGenerator = (itemId: string | number) => `table-row-toggle-${itemId}`

const RcfaComplianceHierarchyTableComponent = ({
  data,
  getIsExpanded,
  toggleExpandCollapse,
  onPlantItemClick
}: Props) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)

  const onItemToggled = useCallback(
    (item: RcfaComplianceTableData) => toggleExpandCollapse(item.id),
    [toggleExpandCollapse]
  )
  const isItemExpanded = useCallback(
    (it: RcfaComplianceTableData) => getIsExpanded(it.id),
    [getIsExpanded]
  )
  const getItemProps = useCallback(
    (item: RcfaComplianceTableData) =>
      item.type === HierarchyNodeType.Plant
        ? {
            onClick: ({id: plantUpmId}: RcfaComplianceTableData) => {
              onPlantItemClick(plantUpmId)
            }
          }
        : {},
    [onPlantItemClick]
  )

  return (
    <TableContainer data-test-id="rcfa-compliance-hierarchy-table">
      <Table sx={{width: '100%'}}>
        <TableHead>
          <TableRow>
            <TableCell>
              <Box sx={expandableTableHeaderCellSx(true)}>{t('label.location')}</Box>
            </TableCell>
            <TableCell>
              <HeaderWithTooltip
                headerText={getCompletedMandatoryRcfasText(t)}
                tooltipText={t('tooltip.rcfaScore')}
              />
            </TableCell>
            <TableCell />
            <TableCell>
              <HeaderWithTooltip
                headerText={t('label.unresolvedTasks')}
                tooltipText={t('tooltip.unresolvedTasks')}
                sx={{justifyContent: 'flex-end'}}
              />
            </TableCell>
            <TableCell>
              <HeaderWithTooltip
                headerText={t('label.overdueTasks')}
                tooltipText={t('tooltip.overdueTasks')}
                sx={{justifyContent: 'flex-end'}}
              />
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((item) => (
            <CollapsableRow
              key={item.id}
              item={item}
              columnKeys={RCFA_COMPLIANCE_TABLE_KEYS}
              renderCell={renderRcfaComplianceTableCell}
              collapseToggleTestIdGenerator={collapseToggleTestIdGenerator}
              isItemExpanded={isItemExpanded}
              onExpandToggleClicked={onItemToggled}
              itemPropsFn={getItemProps}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export const RcfaComplianceHierarchyTable = React.memo(
  RcfaComplianceHierarchyTableComponent,
  isEqual
)
